import React from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import "./index.css";
import { formatMomentDate } from "../../utils/dateHelper";
import { Calendar } from "lucide-react";

const CustomDateRange = ({
  startDate,
  endDate,
  onApply,
  onClear,
  label = "Select Date Range",
  value,
  onChange,
  name,
  placeholder,
  className = "",
  ...rest
}) => {
  // Resolve startDate and endDate from value (supporting object, array, or string formats)
  let resolvedStartDate = startDate;
  let resolvedEndDate = endDate;

  if (value) {
    if (typeof value === "object") {
      if (Array.isArray(value)) {
        resolvedStartDate = value[0];
        resolvedEndDate = value[1];
      } else {
        resolvedStartDate = value.start || value.startDate || value.start_date;
        resolvedEndDate = value.end || value.endDate || value.end_date;
      }
    } else if (typeof value === "string") {
      const parts = value.split(/ to |,|\s*-\s*/);
      if (parts.length === 2) {
        resolvedStartDate = parts[0].trim();
        resolvedEndDate = parts[1].trim();
      }
    }
  }

  const getPastDate = (days = 0, years = 0) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    d.setFullYear(d.getFullYear() - years);
    return d;
  };

  const ranges = {
    "Today Only": [new Date(), new Date()],
    "Yesterday Only": [getPastDate(1), getPastDate(1)],
    "3 Days": [getPastDate(2), new Date()],
    "5 Days": [getPastDate(4), new Date()],
    "1 Week": [getPastDate(6), new Date()],
    "2 Weeks": [getPastDate(13), new Date()],
    "1 Month": [getPastDate(29), new Date()],
    "90 Days": [getPastDate(89), new Date()],
    "1 Year": [getPastDate(0, 1), new Date()],
  };

  const handleApply = (event, picker) => {
    const startStr = picker.startDate.format("YYYY-MM-DD");
    const endStr = picker.endDate.format("YYYY-MM-DD");

    if (onApply) {
      onApply(startStr, endStr);
    }

    if (onChange) {
      onChange({
        target: {
          name,
          value: {
            start: startStr,
            end: endStr,
            startDate: startStr,
            endDate: endStr
          }
        }
      });
    }
  };

  const handleCancel = (event, picker) => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);
    picker.setStartDate(todayStart);
    picker.setEndDate(todayEnd);

    if (picker.container) {
      picker.container.find('.ranges li').removeClass('active');
    }

    picker.updateView();
    picker.updateCalendars();

    if (onClear) {
      onClear();
    }

    if (onChange) {
      onChange({
        target: {
          name,
          value: {
            start: "",
            end: "",
            startDate: "",
            endDate: ""
          }
        }
      });
    }
  };

  const handleShow = (event, picker) => {
    if (!picker.patchedClickRange && picker.container) {
      const rangesContainer = picker.container.find('.ranges');

      // Remove the library's default auto-apply event listener
      rangesContainer.off('click', 'li');

      // Bind our custom listener that DOES NOT call clickApply()
      rangesContainer.on('click', 'li', function (e) {
        var label = this.getAttribute('data-range-key');
        picker.chosenLabel = label;
        if (label === picker.locale.customRangeLabel) {
          picker.showCalendars();

          const pickerMoment = picker.startDate.constructor;
          // Revert back to the originally applied dates (or today if none) when cancelling a predefined range
          if (resolvedStartDate && resolvedEndDate) {
            picker.startDate = pickerMoment(resolvedStartDate).startOf('day');
            picker.endDate = pickerMoment(resolvedEndDate).endOf('day');
          } else {
            picker.startDate = pickerMoment().startOf('day');
            picker.endDate = pickerMoment().endOf('day');
          }

          picker.updateView();
          picker.updateCalendars();

          // Need to set active class for Custom Range too!
          rangesContainer.find('li').removeClass('active');
          this.classList.add('active');
        } else {
          var dates = picker.ranges[label];
          picker.startDate = dates[0].clone();
          picker.endDate = dates[1].clone();

          if (!picker.timePicker) {
            picker.startDate.startOf('day');
            picker.endDate.endOf('day');
          }

          if (!picker.alwaysShowCalendars) {
            picker.hideCalendars();
          }

          picker.updateView();
          picker.updateCalendars();

          rangesContainer.find('li').removeClass('active');
          this.classList.add('active');
        }
      });

      picker.patchedClickRange = true;
    }
  };

  const displayString = resolvedStartDate && resolvedEndDate
    ? `${formatMomentDate(resolvedStartDate, "DD-MM-YYYY")} to ${formatMomentDate(resolvedEndDate, "DD-MM-YYYY")}`
    : placeholder || label;

  return (
    <DateRangePicker
      key={resolvedStartDate && resolvedEndDate ? "has-dates" : "no-dates"}
      initialSettings={{
        ranges,
        alwaysShowCalendars: true,
        linkedCalendars: false,
        autoUpdateInput: false,
        startDate: resolvedStartDate ? new Date(resolvedStartDate) : new Date(),
        endDate: resolvedEndDate ? new Date(resolvedEndDate) : new Date(),
        drops: "auto",
        locale: {
          cancelLabel: 'Clear'
        }
      }}
      onApply={handleApply}
      onCancel={handleCancel}
      onShow={handleShow}
      {...rest}
    >
      <div className={`d-flex justify-content-between align-items-center bg-white cursor-pointer form-control ${className || ""}`}>
        <span className={resolvedStartDate && resolvedEndDate ? "text-primary" : "text-secondary"}>
          {displayString}
        </span>
        <Calendar size={16} className="text-secondary" />
      </div>
    </DateRangePicker>
  );
};

export default CustomDateRange;
